// FRU-23700 (Ed, 10/3 2:02 AM: "Register the quote form as WebMCP without changing the form"). Two WebMCP tools for browser
// agents, the same on the original form and on Hope so neither arm of the A/B is favored:
//   get_rate_quote_step    the question on screen now, with its choices
//   fill_rate_quote_form   answers the questions it has answers for, one at a time, and stops at the last step
// Every answer goes through the step's own submit, the code a tap runs, so skip logic, validation, the ZIP lookup (which
// picks the consent text by state) and analytics behave exactly as they do for a person. The tools never press Get My
// Rates: the person, or the agent they asked to shop for them, reviews the consent text and submits. Nothing here runs
// unless an agent calls a tool, and nothing a person sees changes.

export const QUOTE_TOOL_FILL = 'fill_rate_quote_form';
export const QUOTE_TOOL_STEP = 'get_rate_quote_step';
/** The form field the server reads to mark a lead an AI agent completed for the consumer (fru FRU-23677). */
export const AGENT_ASSISTED_FIELD = 'leadAgentAssisted';

export interface QuoteChoice { value: string; label: string; }

export interface QuoteStep {
  /** The step's key; for a one-field step it is also the answer's field name. */
  key: string;
  question: string;
  kind: 'choice' | 'amount' | 'text' | 'fields';
  /** The answers' field names. One for most steps; two for ZIP and street, or first and last name. */
  fields: string[];
  choices?: QuoteChoice[];
  /** The last step: phone number, the consent text and Get My Rates. The tools stop here. */
  last: boolean;
}

export type QuoteAnswers = { [field: string]: string | number | boolean | null | undefined };

/** One form arm (the original form or Hope) as the tools see it. */
export interface QuoteFormAdapter {
  arm: 'original' | 'hope';
  currentStep(): QuoteStep | null;
  /** Answer the current step through its own submit, as a tap would. Returns '' when applied, or why not. */
  answer(step: QuoteStep, answers: QuoteAnswers): string;
  /** Set the agent flag on the form so the submitted lead carries it. */
  markAgentAssisted(): void;
  /** Put the phone number in the last step's field without submitting. Returns false if it wasn't valid. */
  prefillPhone(phone: string): boolean;
  /** For an answer the current flow can't take (a different loan type on the original form): where to go instead. */
  switchHint?(answers: QuoteAnswers): string;
  /** FRU-25616: the note when prefillPhone refused the number, for a form that leaves its field as it was. Without it the
   *  note says the field is empty. */
  phoneRefusedNote?(): string;
}

export type QuoteFillStatus = 'ready' | 'needs_answer' | 'refused' | 'stuck' | 'unavailable' | 'busy';

export interface QuoteFillResult {
  status: QuoteFillStatus;
  arm?: 'original' | 'hope';
  /** Fields answered by this call, in order. Never their values. */
  answered: string[];
  step?: QuoteStep;
  message: string;
}

/** A choice's value from an answer: the value itself, its label, or yes/no for true/false, case-insensitive. */
export function matchQuoteChoice(choices: QuoteChoice[], answer: any): QuoteChoice | null {
  if (answer === null || answer === undefined || answer === '') { return null; }
  const want = (answer === true ? 'yes' : answer === false ? 'no' : String(answer)).trim().toLowerCase();
  const norm = (s: any) => String(s === null || s === undefined ? '' : s).trim().toLowerCase();
  return choices.find((c) => norm(c.value) === want) || choices.find((c) => norm(c.label) === want) || null;
}

/** A whole-dollar amount from 450000, "450,000", "$450,000" or "450000.00". Null when it isn't a positive amount or zero. */
export function quoteAmount(answer: any): number | null {
  if (typeof answer === 'number') { return isFinite(answer) && answer >= 0 ? Math.round(answer) : null; }
  const s = String(answer === null || answer === undefined ? '' : answer).replace(/[$,\s]/g, '').replace(/\.\d{1,2}$/, '');
  return /^\d{1,9}$/.test(s) ? parseInt(s, 10) : null;
}

/** The fields of the current step the answers don't cover. */
export function missingQuoteFields(step: QuoteStep, answers: QuoteAnswers): string[] {
  return step.fields.filter((f) => {
    const v = answers ? answers[f] : undefined;
    return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
  });
}

function describeForAgent(step: QuoteStep): string {
  const choices = step.choices && step.choices.length
    ? ' Choices: ' + step.choices.map((c) => c.value + (c.label && c.label !== c.value ? ' (' + c.label + ')' : '')).join(', ') + '.'
    : '';
  const fields = step.fields.length ? ' answers ' + step.fields.join(' and ') + '.' : ' needs no answer from you; the fill continues past it.';
  return '"' + step.question + '"' + fields + choices;
}

export interface QuoteFillOptions {
  maxSteps?: number;
  /** How long to wait for the form to move on after an answer. */
  settleMs?: number;
  pollMs?: number;
  wait?: (ms: number) => Promise<void>;
  signal?: { aborted: boolean } | null;
}

const READY_MESSAGE = 'The form is at its last step. The phone number, the consent text and the Get My Rates button are on screen. '
  + 'Nothing has been submitted: the person, or you if they asked you to request quotes for them, reviews the consent text '
  + 'and clicks Get My Rates to send the request.';

/**
 * Answer the current question, then the next, for as long as the answers cover them, through each step's own submit.
 * Stops at the last step (never submitting), at the first question with no answer (returning it), or when an answer is
 * refused or the form doesn't move.
 */
export async function runQuoteFill(adapter: QuoteFormAdapter, answers: QuoteAnswers, opts: QuoteFillOptions = {}): Promise<QuoteFillResult> {
  const maxSteps = opts.maxSteps || 40;
  const settleMs = opts.settleMs || 5000;
  const pollMs = opts.pollMs || 100;
  const wait = opts.wait || ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const answered: string[] = [];
  const done = (r: Omit<QuoteFillResult, 'answered' | 'arm'>): QuoteFillResult => ({ arm: adapter.arm, answered, ...r });
  answers = answers || {};
  const hint = adapter.switchHint ? adapter.switchHint(answers) : '';
  if (hint) { return done({ status: 'refused', message: hint }); }
  try { adapter.markAgentAssisted(); } catch (e) { /* the fill still works; the lead just isn't marked */ }
  const sig = (s: QuoteStep) => JSON.stringify([s.key, s.question, s.fields, (s.choices || []).map((c) => c.value)]);
  // A question on screen that differs from `before` (any question when before is ''). Between questions a form can briefly
  // show none (Hope animates the next one in), so keep looking until settleMs runs out.
  const nextStep = async (before: string): Promise<QuoteStep | null> => {
    for (let t = 0; t <= settleMs; t += pollMs) {
      const now = adapter.currentStep();
      if (now && sig(now) !== before) { return now; }
      if (opts.signal && opts.signal.aborted) { return null; }
      await wait(pollMs);
    }
    return null;
  };
  let step = await nextStep('');
  for (let i = 0; i < maxSteps; i++) {
    if (opts.signal && opts.signal.aborted) { return done({ status: 'stuck', message: 'Stopped: the call was cancelled.' }); }
    if (!step) { return done({ status: 'unavailable', message: 'The quote form is not on screen right now.' }); }
    if (step.last) {
      const phone = answers['phone'] !== undefined ? answers['phone'] : answers['phoneNumber'];
      let note = '';
      if (phone !== undefined && phone !== null && String(phone).trim() !== '') {
        const written = adapter.prefillPhone(String(phone));
        const refused = adapter.phoneRefusedNote ? adapter.phoneRefusedNote()
          : ' The phone number given was not a valid 10-digit U.S. number, so the field is empty.';
        note = written ? ' The phone number is filled in.' : refused;
      }
      return done({ status: 'ready', step, message: READY_MESSAGE + note });
    }
    const missing = missingQuoteFields(step, answers);
    if (missing.length) {
      return done({ status: 'needs_answer', step, message: 'The form is asking ' + describeForAgent(step) + ' Call again with ' + missing.join(' and ') + ' added to answers.' });
    }
    // Re-read the question just before answering: another call, or the person, may have moved the form since it was read.
    const fresh = adapter.currentStep();
    if (!fresh || sig(fresh) !== sig(step)) {
      step = fresh || await nextStep('');
      continue;
    }
    const why = adapter.answer(step, answers);
    if (why) { return done({ status: 'refused', step, message: 'The form did not take the answer for ' + step.fields.join(' and ') + ': ' + why }); }
    answered.push(...step.fields);
    // Wait for the form to move to another question (or to change the one on screen, e.g. a follow-up choice).
    const moved = await nextStep(sig(step));
    if (!moved) {
      return done({ status: 'stuck', step, message: 'The form did not move on after ' + step.fields.join(' and ') + '. It may be showing a message about that answer; check the page.' });
    }
    step = moved;
  }
  return done({ status: 'stuck', message: 'Stopped after ' + maxSteps + ' questions without reaching the last step.' });
}

/** The question an adapter is showing now is the one it was asked to answer: same step, fields and wording. */
export function sameQuoteStep(a: QuoteStep | null, b: QuoteStep | null): boolean {
  if (!a || !b) { return false; }
  return a.key === b.key && a.question === b.question && JSON.stringify(a.fields) === JSON.stringify(b.fields)
    && JSON.stringify((a.choices || []).map((c) => c.value)) === JSON.stringify((b.choices || []).map((c) => c.value));
}

/** The current question for an agent, or null when the form isn't on screen. */
export function describeQuoteStep(adapter: QuoteFormAdapter | null): { arm?: string; step: QuoteStep | null; message: string } {
  const step = adapter ? adapter.currentStep() : null;
  if (!adapter || !step) { return { step: null, message: 'The quote form is not on screen right now.' }; }
  return { arm: adapter.arm, step, message: step.last ? READY_MESSAGE : 'The form is asking ' + describeForAgent(step) };
}

/** The answers' JSON Schema. Every field is optional: the fill stops at the first question it has no answer for. */
export const QUOTE_INPUT_SCHEMA = {
  type: 'object',
  properties: {
    answers: {
      type: 'object',
      description: 'Answers for the person you are helping, by field name. Give the ones you know; the form stops at the first '
        + 'question it has no answer for and says which field it needs, with its choices.',
      properties: {
        loanPurpose: { type: 'string', enum: ['purchase', 'refinance', 'heloc', 'cashout', 'homeimprovement'], description: 'What the loan is for.' },
        propertyType: { type: 'string', enum: ['Single', 'Multi', 'Condo', 'Town House', 'Cooperative', 'Mobile'] },
        propertyUse: { type: 'string', enum: ['primary', 'secondary', 'investment'] },
        propertyValue: { type: 'integer', description: 'Home value in dollars (refinance and home equity).' },
        firstMortgageBalance: { type: 'integer', description: 'What is still owed on the mortgage, in dollars; 0 if paid off.' },
        cashOut: { type: 'integer', description: 'Cash wanted from the home, in dollars.' },
        cashOutReason: { type: 'string', enum: ['reduce-debt', 'home-improvement', 'investment', 'education', 'medical', 'emergency', 'extra-cash', 'other'] },
        newHomeValue: { type: 'integer', description: 'Purchase price in dollars.' },
        estimatedDownPayment: { type: 'number', description: 'Down payment as a percent of the price.' },
        creditRating: { type: 'string', enum: ['Excellent', 'Good', 'Fair', 'Poor'] },
        veteranMilitary: { type: 'boolean', description: 'Served in the U.S. military.' },
        currentFHA: { type: 'boolean', description: 'The current mortgage is an FHA loan.' },
        currentVA: { type: 'boolean', description: 'The current mortgage is a VA loan.' },
        firstTimeBuyer: { type: 'boolean' },
        buyingProcess: { type: 'string', enum: ['just_thinking', 'days_from_buying', 'days_from_buying_30', 'found_home', 'signed_agreement'], description: 'Where the buyer is: researching, buying within 90 or 30 days, found a home, or signed an agreement.' },
        zipCode: { type: 'string', pattern: '^\\d{5}$', description: 'The property ZIP code.' },
        streetAddress: { type: 'string', description: 'The property street address.' },
        firstName: { type: 'string' },
        lastName: { type: 'string' },
        email: { type: 'string', format: 'email' },
        phone: { type: 'string', description: '10-digit U.S. phone number. Filled into the last step; never submitted by this tool.' },
        creditScoreOver500: { type: 'boolean', description: 'Asked after creditRating Poor.' },
        creditCardApr: { type: 'number', description: 'Optional: the card rate for the home equity calculator\'s comparison; "Not sure" (24%) is used without it.' },
      },
      additionalProperties: { type: ['string', 'number', 'boolean'] },
    },
  },
  required: ['answers'],
};

export const QUOTE_FILL_DESCRIPTION = 'Fill in FreeRateUpdate\'s free mortgage rate request for the person you are helping, using '
  + 'the answers they gave you. It answers the questions on screen one at a time, exactly as a tap would, and stops at the last '
  + 'step, which holds the phone number, the consent text and the Get My Rates button. It never submits. If a question has no '
  + 'answer in your call, it stops there and tells you the field and its choices; call again with that field added. Free and '
  + 'no obligation; the request goes to up to a few lenders, who contact the person with their offers.';

export const QUOTE_STEP_DESCRIPTION = 'The question the FreeRateUpdate rate request form is asking right now, with the field it '
  + 'answers and its choices. Read-only.';
